import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';

interface User {
  id: string;
  name: string;
  email: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  eventType: string;
  location: string;
  relatedId: string;
  relatedType: string;
  participants: User[];
  createdBy: string;
  createdByName: string;
}

type ViewMode = 'month' | 'week';

const eventTypeLabels: Record<string, string> = {
  meeting: 'ミーティング',
  request: '要請対応',
  construction: '工事',
  personal: '個人予定',
  holiday: '休暇',
  facility: '設備点検',
};

const eventTypeColors: Record<string, string> = {
  meeting: 'bg-green-500',
  request: 'bg-yellow-500',
  construction: 'bg-blue-500',
  personal: 'bg-purple-500',
  holiday: 'bg-red-500',
  facility: 'bg-orange-500',
};

const initialFormData = {
  title: '',
  description: '',
  startTime: '',
  endTime: '',
  allDay: false,
  eventType: 'meeting',
  location: '',
  relatedId: '',
  relatedType: '',
  participantIds: [] as string[],
};

export default function Calendar() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(() => {
    const y = searchParams.get('year');
    const m = searchParams.get('month');
    if (y && m) return new Date(Number(y), Number(m) - 1, 1);
    return new Date();
  });
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const v = searchParams.get('view');
    return v === 'week' ? 'week' : 'month';
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchUsers();
  }, [currentDate, viewMode]);

  useEffect(() => {
    setSearchParams(prev => {
      prev.set('year', String(currentDate.getFullYear()));
      prev.set('month', String(currentDate.getMonth() + 1));
      prev.set('view', viewMode);
      return prev;
    }, { replace: true });
  }, [currentDate, viewMode, setSearchParams]);

  const fetchEvents = async () => {
    setLoading(true);
    let start: Date, end: Date;

    if (viewMode === 'month') {
      start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    } else {
      const dayOfWeek = currentDate.getDay();
      start = new Date(currentDate);
      start.setDate(currentDate.getDate() - dayOfWeek);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    }

    try {
      const data = await api.get<{ items: CalendarEvent[] }>(
        `/api/calendar?start=${start.toISOString()}&end=${end.toISOString()}`
      );
      setEvents(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      setAvailableUsers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleParticipantChange = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      participantIds: prev.participantIds.includes(userId)
        ? prev.participantIds.filter(id => id !== userId)
        : [...prev.participantIds, userId],
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post('/api/calendar', formData);
      setShowCreateModal(false);
      setFormData(initialFormData);
      fetchEvents();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('予定の作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent || submitting) return;

    setSubmitting(true);
    try {
      await api.put(`/api/calendar/${selectedEvent.id}`, formData);
      setShowEditModal(false);
      setSelectedEvent(null);
      setFormData(initialFormData);
      fetchEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('予定の更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!selectedEvent) return;
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await api.delete(`/api/calendar/${selectedEvent.id}`);
      setShowDetailModal(false);
      setSelectedEvent(null);
      fetchEvents();
      toast.success('予定を削除しました');
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('予定の削除に失敗しました');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;
    setFormData({
      title: selectedEvent.title,
      description: selectedEvent.description || '',
      startTime: selectedEvent.startTime.slice(0, 16),
      endTime: selectedEvent.endTime.slice(0, 16),
      allDay: selectedEvent.allDay,
      eventType: selectedEvent.eventType,
      location: selectedEvent.location || '',
      relatedId: selectedEvent.relatedId || '',
      relatedType: selectedEvent.relatedType || '',
      participantIds: selectedEvent.participants?.map(p => p.id) || [],
    });
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

    const startWeekday = firstDay.getDay();
    for (let i = startWeekday - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const endWeekday = lastDay.getDay();
    for (let i = 1; i < 7 - endWeekday; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  };

  const getWeekDays = () => {
    const dayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().slice(0, 10);
    setFormData(prev => ({
      ...prev,
      startTime: `${dateStr}T09:00`,
      endTime: `${dateStr}T10:00`,
    }));
    setShowCreateModal(true);
  };

  const getTitle = () => {
    if (viewMode === 'month') {
      return `${currentDate.getFullYear()}年 ${currentDate.getMonth() + 1}月`;
    } else {
      const days = getWeekDays();
      const start = days[0];
      const end = days[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getFullYear()}年 ${start.getMonth() + 1}月 ${start.getDate()}日 - ${end.getDate()}日`;
      }
      return `${start.getMonth() + 1}/${start.getDate()} - ${end.getMonth() + 1}/${end.getDate()}`;
    }
  };

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  const renderForm = (onSubmit: (e: React.FormEvent) => void, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <Input
        label="タイトル"
        name="title"
        value={formData.title}
        onChange={handleChange}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
          rows={2}
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="allDay"
          name="allDay"
          checked={formData.allDay}
          onChange={handleChange}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="allDay" className="text-sm text-gray-700 dark:text-gray-300">終日</label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="開始日時"
          name="startTime"
          type={formData.allDay ? 'date' : 'datetime-local'}
          value={formData.allDay ? formData.startTime.slice(0, 10) : formData.startTime}
          onChange={handleChange}
          required
        />
        <Input
          label="終了日時"
          name="endTime"
          type={formData.allDay ? 'date' : 'datetime-local'}
          value={formData.allDay ? formData.endTime.slice(0, 10) : formData.endTime}
          onChange={handleChange}
          required
        />
      </div>
      <Select label="種別" name="eventType" value={formData.eventType} onChange={handleChange}>
        {Object.entries(eventTypeLabels).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </Select>
      <Input
        label="場所"
        name="location"
        value={formData.location}
        onChange={handleChange}
        placeholder="オンライン / 会議室A など"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select label="関連種別" name="relatedType" value={formData.relatedType} onChange={handleChange}>
          <option value="">なし</option>
          <option value="request">要請応対</option>
          <option value="construction">工事</option>
        </Select>
        {formData.relatedType && (
          <Input
            label="関連ID"
            name="relatedId"
            value={formData.relatedId}
            onChange={handleChange}
            placeholder="関連するIDを入力"
          />
        )}
      </div>

      {/* 参加者選択 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">参加者</label>
        <div className="max-h-40 overflow-y-auto border dark:border-gray-700 rounded-lg p-2 space-y-1">
          {availableUsers.map(user => (
            <label key={user.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={formData.participantIds.includes(user.id)}
                onChange={() => handleParticipantChange(user.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900 dark:text-white">{user.name}</span>
              <span className="text-xs text-gray-500">({user.email})</span>
            </label>
          ))}
        </div>
        {formData.participantIds.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">{formData.participantIds.length}名選択中</p>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setFormData(initialFormData);
          }}
          className="w-full sm:w-auto"
        >
          キャンセル
        </Button>
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? '処理中...' : isEdit ? '更新' : '登録'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">カレンダー</h1>
        <div className="flex gap-2">
          {/* 表示切替 */}
          <div className="flex border dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'month' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              月
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'week' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              週
            </button>
          </div>
          {canCreate('calendar') && (
            <Button onClick={() => { setSelectedDate(new Date()); handleDateClick(new Date()); }}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              予定を追加
            </Button>
          )}
        </div>
      </div>

      <Card>
        {/* ナビゲーション */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handlePrev}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button variant="secondary" size="sm" onClick={handleToday}>今日</Button>
            <Button variant="ghost" onClick={handleNext}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {getTitle()}
          </h2>
          <div className="w-24" />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : viewMode === 'month' ? (
          /* 月表示 */
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
            {weekDays.map((day, index) => (
              <div
                key={day}
                className={`p-1 sm:p-2 text-center text-xs sm:text-sm font-medium bg-gray-50 dark:bg-gray-800 ${
                  index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {day}
              </div>
            ))}
            {getDaysInMonth().map(({ date, isCurrentMonth }, index) => {
              const dayEvents = getEventsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const weekday = date.getDay();

              return (
                <div
                  key={index}
                  onClick={() => handleDateClick(date)}
                  className={`min-h-[60px] sm:min-h-[100px] p-1 sm:p-2 cursor-pointer transition-colors ${
                    isCurrentMonth
                      ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'bg-gray-50 dark:bg-gray-900'
                  }`}
                >
                  <div className={`text-xs sm:text-sm font-medium mb-1 ${
                    !isCurrentMonth ? 'text-gray-400' :
                    weekday === 0 ? 'text-red-500' :
                    weekday === 6 ? 'text-blue-500' :
                    'text-gray-900 dark:text-white'
                  }`}>
                    <span className={`${isToday ? 'bg-blue-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 inline-flex items-center justify-center text-xs sm:text-sm' : ''}`}>
                      {date.getDate()}
                    </span>
                  </div>
                  <div className="space-y-0.5 sm:space-y-1">
                    {dayEvents.slice(0, 2).map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={`text-xs px-1 py-0.5 rounded truncate text-white cursor-pointer hover:opacity-80 ${eventTypeColors[event.eventType] || 'bg-gray-500'}`}
                      >
                        <span className="hidden sm:inline">{event.title}</span>
                        <span className="sm:hidden">{event.title.slice(0, 4)}</span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-500">+{dayEvents.length - 2}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 週表示 */
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
            {getWeekDays().map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const weekday = date.getDay();

              return (
                <div key={index} className="bg-white dark:bg-gray-800">
                  {/* 日付ヘッダー */}
                  <div
                    onClick={() => handleDateClick(date)}
                    className={`p-2 text-center border-b dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      weekday === 0 ? 'text-red-500' : weekday === 6 ? 'text-blue-500' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="text-xs">{weekDays[weekday]}</div>
                    <div className={`text-lg font-semibold ${isToday ? 'bg-blue-500 text-white rounded-full w-8 h-8 mx-auto flex items-center justify-center' : ''}`}>
                      {date.getDate()}
                    </div>
                  </div>
                  {/* イベント */}
                  <div className="min-h-[200px] p-1 space-y-1">
                    {dayEvents.map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={`text-xs p-1.5 rounded text-white cursor-pointer hover:opacity-80 ${eventTypeColors[event.eventType] || 'bg-gray-500'}`}
                      >
                        <div className="font-medium truncate">{event.title}</div>
                        {!event.allDay && (
                          <div className="text-white/80 text-xs">
                            {new Date(event.startTime).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* イベント種別の凡例 */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(eventTypeLabels).map(([type, label]) => (
          <div key={type} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${eventTypeColors[type]}`} />
            <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
          </div>
        ))}
      </div>

      {/* 作成モーダル */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setFormData(initialFormData); }} title="予定を追加" size="lg">
        {renderForm(handleCreate)}
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedEvent(null); setFormData(initialFormData); }} title="予定を編集" size="lg">
        {renderForm(handleUpdate, true)}
      </Modal>

      {/* 詳細モーダル */}
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedEvent(null); }} title="予定の詳細" size="md">
        {selectedEvent && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className={`w-4 h-4 rounded mt-1 flex-shrink-0 ${eventTypeColors[selectedEvent.eventType]}`} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedEvent.title}</h3>
                <Badge variant="default">{eventTypeLabels[selectedEvent.eventType]}</Badge>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {selectedEvent.allDay ? (
                  <span>
                    {new Date(selectedEvent.startTime).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                    {selectedEvent.startTime !== selectedEvent.endTime && ` - ${new Date(selectedEvent.endTime).toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' })}`}
                    <span className="ml-1 text-xs">(終日)</span>
                  </span>
                ) : (
                  <span>
                    {new Date(selectedEvent.startTime).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {' - '}
                    {new Date(selectedEvent.endTime).toLocaleString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{selectedEvent.location}</span>
                </div>
              )}

              {selectedEvent.description && (
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.participants && selectedEvent.participants.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">参加者 ({selectedEvent.participants.length}名)</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedEvent.participants.map(p => (
                      <span key={p.id} className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded text-xs">
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 pt-2 border-t dark:border-gray-700">
                作成者: {selectedEvent.createdByName}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
              <Button variant="danger" onClick={handleDelete}>削除</Button>
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>閉じる</Button>
              <Button onClick={handleEditEvent}>編集</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">この予定を削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDeleteEvent}>削除</Button>
        </div>
      </Modal>
    </div>
  );
}

